// Per-user workspace snapshot — the open connection tabs and their inner tabs.
// Stored server-side as an opaque JSON string and restored on app start.
import { api } from './api.js';
import { toast } from './toast.js';

// The saved snapshot, or null when none is saved (or it's unreadable JSON —
// nothing to restore). A failed REQUEST throws instead: the caller must not
// read it as "no workspace", or the next save would erase the stored copy.
export async function loadWorkspace() {
  const r = await api.get('/api/me/workspace');
  if (!r || !r.data) return null;
  try { return JSON.parse(r.data); } catch { return null; }
}

// The server's cap on a stored snapshot, in UTF-8 bytes (maxWorkspaceBytes in
// internal/api/workspace.go). A bigger PUT is rejected.
export const MAX_WORKSPACE_BYTES = 1 << 20;

// fitWorkspace returns the snapshot JSON to store: `json` (the snapshot already
// serialized) when it fits under `max`, else a re-serialization with each query
// tab's message history — the usual bulk — cut to its last 50 entries, then
// dropped. null when even that doesn't fit (huge SQL / very many tabs).
export function fitWorkspace(snap, json = JSON.stringify(snap), max = MAX_WORKSPACE_BYTES) {
  const fits = (s) => s.length * 3 <= max || new TextEncoder().encode(s).length <= max;
  if (fits(json)) return json;
  for (const keep of [50, 0]) {
    const states = {};
    for (const [id, st] of Object.entries(snap.states || {})) {
      states[id] = { ...st, queryTabs: (st.queryTabs || []).map((q) =>
        (Array.isArray(q.messages) ? { ...q, messages: keep ? q.messages.slice(-keep) : [] } : q)) };
    }
    const trimmed = JSON.stringify({ ...snap, states });
    if (fits(trimmed)) return trimmed;
  }
  return null;
}

// `dataString` is the snapshot from fitWorkspace, '' to clear the workspace, or
// null when it was too large to store. Persistence failing never disrupts the
// UI, but it isn't silent either: the first failure in a row shows one toast
// (a successful save re-arms it), so a workspace that stops saving is noticed.
let failureShown = false;
export async function saveWorkspaceData(dataString) {
  if (dataString == null) {
    if (!failureShown) toast.error('Your workspace is too large to save (over 1 MiB, even without run messages) — close some tabs. The last saved copy is kept.');
    failureShown = true;
    return;
  }
  try {
    await api.put('/api/me/workspace', { data: dataString });
    failureShown = false;
  } catch (e) {
    if (e?.message === 'unauthorized') return; // session gone — already redirecting to /login
    if (!failureShown) toast.error(`Couldn't save your workspace: ${e?.message || e}`);
    failureShown = true;
  }
}
